import { Divider, Link, Typography } from '@material-ui/core';
import { WarningRounded } from '@material-ui/icons';
import { FormikTextField } from '@superdispatch/forms';
import {
  Color,
  ColorDynamic,
  Column,
  Columns,
  Image,
  Stack,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { ChangeEvent } from 'react';
import { ErrorMessageBox } from 'shared/errors/SuperPayBankErrors';
import { useAppFormik } from 'shared/form/AppFormik';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { SuperPayBankAccountDTO } from 'shared/modules/superpay/SuperPayDTO';
import { useCarrierSettingsCache } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';
import microDepositIllustration from '../../assets/micro_deposit_illustration.png';
import microDepositIllustrationDark from '../../assets/micro_deposit_illustration_dark.png';
import {
  INSTANT_VERIFICATION_CODE_LENGTH,
  instantVerificationSchema,
} from '../../data/MoovDTO';
import { useCompleteInstantBankAccountVerification } from '../../data/MoovAPI';
import {
  useGenerateMoovTokenAndGetAccount,
  useSuperPaySettings,
  useSuperPayValidationIncrement,
} from '../../data/SuperPayAPI';
import { useTrackChangeBankAccountEvent } from './ChangeBankAccountAnalytics';
import { getStatusErrors } from './getStatusErrors';
import { useCloseOnLocked } from './useCloseOnLocked';

const StatementCard = styled(Box)`
  border-radius: 8px;
  border: 1px solid ${ColorDynamic.Silver400};
`;

const BlurredStatementText = styled.span`
  display: inline-block;
  filter: blur(5px);
  user-select: none;
`;

const VERIFICATION_LOCK_THRESHOLD = 2;

export function getInstantVerificationState(
  bankAccount?: SuperPayBankAccountDTO,
): { isLastAttempt: boolean; isLocked: boolean } {
  const errorCount = bankAccount?.verification_error_count ?? 0;
  return {
    isLastAttempt: errorCount === 1,
    isLocked:
      errorCount >= VERIFICATION_LOCK_THRESHOLD ||
      bankAccount?.verification_status === 'locked',
  };
}

interface UseInstantBankVerificationFormProps {
  onComplete: () => void;
}

export function useInstantBankVerificationForm({
  onComplete,
}: UseInstantBankVerificationFormProps) {
  const { mutateAsync: completeInstantVerification } =
    useCompleteInstantBankAccountVerification();
  const { mutateAsync: incrementValidationError } =
    useSuperPayValidationIncrement();
  const { invalidateCarrierSettings } = useCarrierSettingsCache();
  const { data: moovInformation } =
    useGenerateMoovTokenAndGetAccount('bank_account');
  const bankAccountID = moovInformation?.moov_bank_account_id;

  return useAppFormik({
    initialValues: { code: '' },
    validationSchema: instantVerificationSchema,
    onSubmit: ({ code }) => completeInstantVerification(code),
    onSubmitSuccess: () => {
      invalidateCarrierSettings();
      onComplete();
    },
    onSubmitFailure: (error) => {
      const { isIncorrect, isExceeded } = getStatusErrors({ payload: error });
      if ((isIncorrect || isExceeded) && bankAccountID) {
        void incrementValidationError(bankAccountID);
      }
    },
    getSuccessMessage: () => 'Bank account verified',
    getErrorMessage: (error) => {
      const { isStatusError } = getStatusErrors({ payload: error });
      return isStatusError ? null : error.message;
    },
  });
}

interface UseInstantBankVerificationStepProps {
  bankAccount?: SuperPayBankAccountDTO;
  onComplete: () => void;
  onLocked?: () => void;
}

export function useInstantBankVerificationStep({
  bankAccount,
  onComplete,
  onLocked,
}: UseInstantBankVerificationStepProps) {
  const formik = useInstantBankVerificationForm({ onComplete });
  const { isFetching } = useSuperPaySettings();
  const { isLocked } = getInstantVerificationState(bankAccount);
  const isCodeComplete =
    formik.values.code.length === INSTANT_VERIFICATION_CODE_LENGTH;

  useCloseOnLocked({
    isLocked,
    submitCount: formik.submitCount,
    onClose: onLocked,
  });

  return {
    formik,
    isLocked,
    isSubmitDisabled: isLocked || !isCodeComplete || isFetching,
  };
}

function StatementExample() {
  return (
    <StatementCard>
      <Box
        paddingTop="xsmall"
        paddingBottom="xsmall"
        paddingLeft="small"
        paddingRight="small"
        backgroundColor="Silver200"
      >
        <Typography align="center" variant="caption" color="textSecondary">
          Find below in your bank app
        </Typography>
      </Box>
      <Divider />
      <Box
        paddingTop="xsmall"
        paddingBottom="xsmall"
        paddingLeft="small"
        paddingRight="small"
      >
        <Columns align="center" space="small">
          <Column>
            <Typography color="textSecondary">
              <BlurredStatementText>SD Credit ACH</BlurredStatementText>{' '}
              <b>MV****</b>
            </Typography>
          </Column>
          <Column width="content">
            <Typography color="textSecondary">$0.01</Typography>
          </Column>
        </Columns>
      </Box>
    </StatementCard>
  );
}

interface InstantBankVerificationFormContentProps {
  bankAccount?: SuperPayBankAccountDTO;
  showExpectTransactionHint?: boolean;
  titleOnTop?: boolean;
}

interface ICantFindMvCodeLinkProps {
  isLocked: boolean;
  onClick: () => void;
}

export function ICantFindMvCodeLink({
  isLocked,
  onClick,
}: ICantFindMvCodeLinkProps) {
  const isScreenshotUploadEnabled = useFlag(
    'payments_superpay_micro_deposit_screenshot',
  );

  if (!isScreenshotUploadEnabled || isLocked) return null;

  return (
    <Link
      color="primary"
      component="button"
      type="button"
      onClick={onClick}
      underline="none"
    >
      I Can’t Find the MV Code
    </Link>
  );
}

export function InstantBankVerificationFormContent({
  bankAccount,
  showExpectTransactionHint = true,
  titleOnTop = false,
}: InstantBankVerificationFormContentProps) {
  const { showLauncherMessanger } = useCustomerSupport();
  const trackChangeBankAccountEvent = useTrackChangeBankAccountEvent();
  const { isLastAttempt, isLocked } = getInstantVerificationState(bankAccount);

  function parseCode(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const digits = event.target.value.replace(/\D/g, '');
    return digits.slice(0, INSTANT_VERIFICATION_CODE_LENGTH);
  }

  const illustration = (
    <Image
      src={microDepositIllustration}
      srcDark={microDepositIllustrationDark}
      width="133px"
      alt="micro deposit illustration"
    />
  );

  const heading = (
    <Typography variant="h3" align="left">
      Micro-deposit Initiated. Please wait a few minutes
    </Typography>
  );

  const hint = showExpectTransactionHint && (
    <Typography align="left" variant="body2">
      You should expect one small transaction to appear in your bank account.
    </Typography>
  );

  return (
    <Stack space="large">
      {titleOnTop ? (
        <Stack space="small">
          {heading}
          <Stack space="xsmall" align="center">
            {illustration}
          </Stack>
          {hint}
        </Stack>
      ) : (
        <Stack space="xsmall" align="center">
          {illustration}
          <Stack space="none">{heading}</Stack>
          {hint}
        </Stack>
      )}

      <StatementExample />

      <Columns space="xsmall">
        <Column width="content">
          <Box position="relative" top="2px">
            <WarningRounded fontSize="small" htmlColor={Color.Yellow300} />
          </Box>
        </Column>
        <Column>
          <Stack space="xsmall">
            <Typography>
              Open your bank app, and go to your transaction history. Find the{' '}
              <b>$0.01</b> transaction from <b>Super Dispatch</b>. Its
              description includes a code that looks like <b>MV****</b>. Enter
              the full code in the field below.
            </Typography>
            <Link
              href="https://support.superdispatch.com/hc/articles/36590363663251-SuperPay-Micro-Transactions"
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              color="primary"
            >
              Learn how to verify micro-deposits.
            </Link>
          </Stack>
        </Column>
      </Columns>

      <Stack space="small">
        <FormikTextField
          label="Enter the MV**** code below"
          name="code"
          fullWidth={true}
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={isLocked}
          error={isLastAttempt || isLocked}
          parse={parseCode}
        />

        {isLocked ? (
          <ErrorMessageBox
            message="Too many failed attempts of micro-deposits verification. Contact support for assistance."
            action={
              <Button
                variant="neutral"
                size="small"
                onClick={() => {
                  trackChangeBankAccountEvent(
                    'Carrier Clicked Contact Support After Verification Locked',
                  );
                  showLauncherMessanger();
                }}
              >
                Contact Support
              </Button>
            }
          />
        ) : isLastAttempt ? (
          <ErrorMessageBox
            message={
              <Box>
                Wrong code. Only <b>ONE</b> attempt left. Make sure the
                verification code is correct while verifying.
              </Box>
            }
          />
        ) : null}
      </Stack>
    </Stack>
  );
}
